function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useContext, forwardRef } from 'react';
import cn from 'clsx';
import { string, bool, func, oneOfType, number } from 'prop-types';
import FormContext from '../../context/FormContext.js';
import FormGroupContext from '../../context/FormGroupContext.js';
import FormLabel from '../FormLabel/index.jsx';

/**
 * FormCheckbox component.
 */
const FormCheckbox = /*#__PURE__*/forwardRef(({
  className,
  label,
  name,
  checked,
  defaultChecked,
  indeterminate,
  onChange,
  rightToLeft,
  largeLabel,
  fluid,
  dataItemName,
  ...restProps
}, ref) => {
  const {
    inputId,
    feedbackId
  } = useContext(FormGroupContext);
  const {
    inverted,
    errors,
    validateInput,
    onChange: onFormChange,
    defaultValues
  } = useContext(FormContext);
  const handleChange = evt => {
    if (onChange) {
      onChange(evt);
    }
    if (onFormChange) {
      onFormChange(evt);
    }
  };
  const Component = label ? FormLabel : 'div';

  // This sets dataItemName and dataTestId differently depending
  // on whether the component is a FormLabel or a div.
  const dataAttributes = label ? {
    dataItemName,
    dataTestId: 'form-checkbox'
  } : {
    'data-item-name': dataItemName,
    'data-testid': 'form-checkbox'
  };

  // Use value for a controlled and defaultValue for an uncontrolled element.
  // https://reactjs.org/docs/forms.html#controlled-components
  const checkedProp = checked != null ? {
    checked
  } : {
    defaultChecked: defaultChecked || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name])
  };
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cn("form-checkbox d-flex", className, inverted && 'form-checkbox--inverted', errors && name in errors && 'form-checkbox--invalid', indeterminate && 'form-checkbox--indeterminate', fluid && 'full-width justify-content-space-between', rightToLeft && 'flex-direction-row-reverse')
  }, dataAttributes), /*#__PURE__*/React.createElement("input", _extends({}, restProps, checkedProp, {
    className: "form-checkbox__input",
    id: inputId,
    name: name,
    ref: ref,
    type: "checkbox",
    onChange: validateInput ? evt => validateInput(evt, handleChange) : handleChange,
    "aria-describedby": feedbackId,
    "data-component": "FormCheckbox"
  })), label && /*#__PURE__*/React.createElement("span", {
    className: cn(rightToLeft ? 'mr-xs' : 'ml-xs', largeLabel && 'body-copy')
  }, label));
});
FormCheckbox.displayName = 'FormCheckbox';
FormCheckbox.defaultProps = {
  checked: null,
  className: null,
  indeterminate: false,
  label: null,
  name: null,
  defaultChecked: null,
  onChange: null,
  rightToLeft: false,
  largeLabel: false,
  fluid: false,
  dataItemName: null
};
FormCheckbox.propTypes = process.env.NODE_ENV !== "production" ? {
  /** The checked value for when using as a controlled component. */
  checked: bool,
  /** Additional class names to add to the component root. */
  className: string,
  /** Mark the input as "indeterminate". */
  indeterminate: bool,
  /** The label for the checkbox */
  label: string,
  /** The input name. */
  name: oneOfType([number, string]),
  /** The default checked value for the input field */
  defaultChecked: bool,
  /** A function to call when the input is checked or unchecked. */
  onChange: func,
  /** Reverse the direction of the input and label */
  rightToLeft: bool,
  /** Default body copy size and color for the label */
  largeLabel: bool,
  /** Make the checkbox span the full width of its container. */
  fluid: bool,
  /** The item name data attribute for GA tagging. */
  dataItemName: string
} : {};
export default FormCheckbox;